import { useState, useEffect } from "react";
import { FaPencilAlt, FaUserFriends, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import Modal from "../Modal";

const REGISTROS_POR_PAGINA = 5;

export default function ReporteServicio({ reporte, onActualizado }) {

  const [registroEditar, setRegistroEditar] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    setPagina(1);
  }, [reporte]);

  const inputClass =
    "w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400";

  const formatoMoneda = (valor) =>
    `$${Number(valor || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}`;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRegistroEditar({ ...registroEditar, [name]: value });
  };

  const actualizarRegistro = async () => {

    setGuardando(true);

    const datos = {
      diezmo: Number(registroEditar.diezmo || 0),
      misiones: Number(registroEditar.misiones || 0),
      protemplo: Number(registroEditar.protemplo || 0),
      ofrenda: Number(registroEditar.ofrenda || 0),
      biblia: Number(registroEditar.biblia || 0),
      especial: Number(registroEditar.especial || 0)
    };

    const total =
      datos.diezmo +
      datos.misiones +
      datos.protemplo +
      datos.ofrenda +
      datos.biblia +
      datos.especial;

    try {
      await fetch(`/api/finanzas/${registroEditar.id_entrada}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...datos,
          total
        })
      });

      if (onActualizado) await onActualizado();

      setRegistroEditar(null);

    } catch (error) {
      console.error("Error actualizando registro", error);
    }

    setGuardando(false);
  };

  const totalPaginas = Math.ceil(reporte.length / REGISTROS_POR_PAGINA);
  const inicio = (pagina - 1) * REGISTROS_POR_PAGINA;
  const reportePagina = reporte.slice(inicio, inicio + REGISTROS_POR_PAGINA);
  const inicioRango = reporte.length === 0 ? 0 : inicio + 1;
  const finRango = Math.min(inicio + REGISTROS_POR_PAGINA, reporte.length);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">

      {/* HEADER */}
      <div className="px-6 py-4 border-b border-slate-100">
        <h2 className="text-base font-semibold text-slate-800">Reporte del servicio</h2>
      </div>

      {/* TABLA */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Miembro</th>
              <th className="px-2 py-3 text-right font-semibold whitespace-nowrap">Diezmo</th>
              <th className="px-2 py-3 text-right font-semibold whitespace-nowrap">Misiones</th>
              <th className="px-2 py-3 text-right font-semibold whitespace-nowrap">Pro templo</th>
              <th className="px-2 py-3 text-right font-semibold whitespace-nowrap">Ofrenda</th>
              <th className="px-2 py-3 text-right font-semibold whitespace-nowrap">Biblia</th>
              <th className="px-2 py-3 text-right font-semibold whitespace-nowrap">Especial</th>
              <th className="px-2 py-3 text-right font-semibold whitespace-nowrap">Total</th>
              <th className="px-3 py-3 text-right font-semibold whitespace-nowrap">Acción</th>
            </tr>
          </thead>

          <tbody>
            {reporte.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center py-10 text-slate-400">
                  Selecciona un servicio para ver su reporte
                </td>
              </tr>
            ) : (
              reportePagina.map((r) => (
                <tr
                  key={r.id_entrada}
                  className="border-t border-slate-100 hover:bg-slate-50 transition"
                >
                  <td className="px-4 py-2.5 max-w-[160px]">
                    <span className="flex items-center gap-2 font-medium text-slate-800" title={r.miembro}>
                      <span className="w-6 h-6 rounded-md bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                        <FaUserFriends className="text-[10px]" />
                      </span>
                      <span className="truncate">{r.miembro}</span>
                    </span>
                  </td>
                  <td className="px-2 py-2.5 text-right text-slate-600 whitespace-nowrap">{formatoMoneda(r.diezmo)}</td>
                  <td className="px-2 py-2.5 text-right text-slate-600 whitespace-nowrap">{formatoMoneda(r.misiones)}</td>
                  <td className="px-2 py-2.5 text-right text-slate-600 whitespace-nowrap">{formatoMoneda(r.protemplo)}</td>
                  <td className="px-2 py-2.5 text-right text-slate-600 whitespace-nowrap">{formatoMoneda(r.ofrenda)}</td>
                  <td className="px-2 py-2.5 text-right text-slate-600 whitespace-nowrap">{formatoMoneda(r.biblia)}</td>
                  <td className="px-2 py-2.5 text-right text-slate-600 whitespace-nowrap">{formatoMoneda(r.especial)}</td>
                  <td className="px-2 py-2.5 text-right font-semibold text-indigo-600 whitespace-nowrap">{formatoMoneda(r.total)}</td>
                  <td className="px-3 py-2.5 text-right">
                    <button
                      onClick={() => setRegistroEditar(r)}
                      title="Editar"
                      className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition"
                    >
                      <FaPencilAlt className="text-xs" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINACIÓN */}
      {reporte.length > 0 && (
        <div className="flex justify-between items-center px-4 py-3 border-t border-slate-100">
          <span className="text-xs text-slate-500">
            Mostrando {inicioRango} a {finRango} de {reporte.length} resultados
          </span>

          {totalPaginas > 1 && (
            <div className="flex items-center gap-2">
              <button
                disabled={pagina === 1}
                onClick={() => setPagina(pagina - 1)}
                className="w-7 h-7 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 disabled:opacity-40 hover:bg-slate-50 transition"
              >
                <FaChevronLeft className="text-[10px]" />
              </button>

              <span className="w-7 h-7 flex items-center justify-center rounded-md bg-indigo-600 text-white text-xs font-semibold">
                {pagina}
              </span>

              <button
                disabled={pagina === totalPaginas}
                onClick={() => setPagina(pagina + 1)}
                className="w-7 h-7 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 disabled:opacity-40 hover:bg-slate-50 transition"
              >
                <FaChevronRight className="text-[10px]" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* MODAL */}
      <Modal
        open={!!registroEditar}
        onClose={() => setRegistroEditar(null)}
        title="Editar registro"
      >
        {registroEditar && (
          <div>
            <div className="mb-3">
              <p className="text-xs text-slate-500">Miembro</p>
              <p className="text-sm font-semibold text-slate-800">{registroEditar.miembro}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {[
                { name: "diezmo", label: "Diezmo" },
                { name: "misiones", label: "Misiones" },
                { name: "protemplo", label: "Pro templo" },
                { name: "ofrenda", label: "Ofrenda" },
                { name: "biblia", label: "Biblia" },
                { name: "especial", label: "Especial" }
              ].map((campo) => (
                <div key={campo.name}>
                  <label className="block text-xs font-medium text-slate-700 mb-1">{campo.label}</label>
                  <input
                    type="number"
                    name={campo.name}
                    value={registroEditar[campo.name]}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
              <button
                onClick={() => setRegistroEditar(null)}
                className="px-3.5 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
              >
                Cancelar
              </button>
              <button
                onClick={actualizarRegistro}
                disabled={guardando}
                className="px-3.5 py-1.5 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-medium transition"
              >
                {guardando ? "Guardando..." : "Guardar cambios"}
              </button>
            </div>
          </div>
        )}
      </Modal>

    </div>
  );
}
